import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { User as SupabaseUser, AuthError } from '@supabase/supabase-js';
import { supabase } from '../integrations/supabase/client';
import { setSentryUser, clearSentryUser } from '../utils/sentry';

type Session = Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session'];

/**
 * Extended user type that includes platform tenant information
 * This is the user object exposed through AuthContext
 */
export interface ExtendedUser extends SupabaseUser {
  platformTenantId?: string;
  platformTenantName?: string;
}

interface AuthContextType {
  user: ExtendedUser | null;
  session: Session | null;
  loading: boolean;
  isAuthenticated: boolean;
  /** 
   * @deprecated Use user.platformTenantId instead
   * This property will be removed in a future version.
   * Migration: Replace `tenantId` with `user?.platformTenantId`
   */
  tenantId: string | null;
  /** 
   * @deprecated Use user.platformTenantId instead
   * This property will be removed in a future version.
   * Migration: Replace `platformTenantId` with `user?.platformTenantId`
   */
  platformTenantId: string | null;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  updatePassword: (password: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [platformTenantId, setPlatformTenantId] = useState<string | null>(null);

  const PROFILE_FETCH_TIMEOUT_MS = 2500;

  // Fetch the user's platform tenant ID from the profiles table
  // Simplified: only fetch platform_tenant_id, skip the join that was causing errors
  // The tenant name is not critical for auth - PlatformTenantContext handles full tenant data
  const fetchUserProfile = async (userId: string): Promise<{ platformTenantId?: string; platformTenantName?: string }> => {
    try {
      // IMPORTANT: Never let profile fetch block auth initialization or login.
      // If RLS/misconfig causes a hang, we fall back to no tenant info.
      const queryPromise = supabase
        .from('profiles')
        .select('platform_tenant_id')
        .eq('id', userId)
        .single();

      const timeoutPromise = new Promise<{ data: null; error: { message: string; code: string } }>((resolve) =>
        setTimeout(
          () => resolve({ data: null, error: { message: 'Profile fetch timeout', code: 'TIMEOUT' } }),
          PROFILE_FETCH_TIMEOUT_MS
        )
      );

      const { data, error } = (await Promise.race([queryPromise as unknown as Promise<any>, timeoutPromise])) as {
        data: { platform_tenant_id?: string | null } | null;
        error: { message: string; code?: string } | null;
      };

      if (error) {
        // Don't log as error - this is expected for new users
        // Profile fetch result - expected for new users
        return {};
      }

      return {
        platformTenantId: data?.platform_tenant_id || undefined,
        platformTenantName: undefined // Let PlatformTenantContext handle this
      };
    } catch (error) {
      // Profile fetch exception - non-critical
      return {};
    }
  };

  // Create extended user with platform tenant info
  const createExtendedUser = async (baseUser: SupabaseUser | null): Promise<ExtendedUser | null> => {
    if (!baseUser) return null;

    const profileData = await fetchUserProfile(baseUser.id);
    
    // Store platformTenantId for backwards compatibility
    setPlatformTenantId(profileData.platformTenantId || null);

    return {
      ...baseUser,
      platformTenantId: profileData.platformTenantId,
      platformTenantName: profileData.platformTenantName
    };
  };

  useEffect(() => {
    // Initialize auth
    
    // Get initial session
    const initializeAuth = async () => {
      try {
        // Fetching session
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[AuthContext] Error getting session:', error);
        } else {
          // Session retrieved
          setSession(initialSession);
          
          if (initialSession?.user) {
            const extendedUser = await createExtendedUser(initialSession.user);
            setUser(extendedUser);
          } else {
            setUser(null);
          }
        }
      } catch (error) {
        console.error('[AuthContext] Error initializing auth:', error);
      } finally {
        // Loading complete
        setLoading(false);
      }
    };

    // CRITICAL: Aggressive 5s timeout to prevent infinite spinner
    const timeoutId = setTimeout(() => {
      console.error('[AuthContext] Session initialization timeout after 5s - forcing loading to false');
      localStorage.setItem('auth_init_timeout', new Date().toISOString());
      setLoading(false);
    }, 5000);

    initializeAuth().finally(() => {
      clearTimeout(timeoutId);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event: string, currentSession: typeof session) => {
        setSession(currentSession);
        
        if (currentSession?.user) {
          const extendedUser = await createExtendedUser(currentSession.user);
          setUser(extendedUser);

          // Update Sentry user context
          setSentryUser({
            id: currentSession.user.id,
            email: currentSession.user.email,
          });
        } else {
          setUser(null);
          setPlatformTenantId(null);
          clearSentryUser();
        }
        
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Sign in error:', error);
        return { error };
      }

      // Set session/user immediately so UI can proceed; enrich with profile async.
      setSession(data.session);
      if (data.user) {
        setUser(data.user as ExtendedUser);
        createExtendedUser(data.user)
          .then((extendedUser) => setUser(extendedUser))
          .catch((err) => console.warn('[AuthContext] Failed to enrich user profile:', err));
      }
      return { error: null };
    } catch (error) {
      console.error('Unexpected sign in error:', error);
      return { error: error as AuthError };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl
        }
      });

      if (error) {
        console.error('Sign up error:', error);
        return { error };
      }

      setSession(data.session);
      if (data.user) {
        const extendedUser = await createExtendedUser(data.user);
        setUser(extendedUser);
      }
      return { error: null };
    } catch (error) {
      console.error('Unexpected sign up error:', error);
      return { error: error as AuthError };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Sign out error:', error);
        throw error;
      }

      setSession(null);
      setUser(null);
      setPlatformTenantId(null);
      
      // Clear Sentry user context on logout
      clearSentryUser();
    } catch (error) {
      console.error('Unexpected sign out error:', error);
      throw error;
    }
  };

  const updatePassword = async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password,
      });

      if (error) {
        console.error('Update password error:', error);
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Unexpected update password error:', error);
      return { error: error as AuthError };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    isAuthenticated: !!session,
    tenantId: platformTenantId,
    platformTenantId,
    signIn,
    signUp,
    signOut,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
